import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Page, LayoutSideNavigation, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { Button } from 'antd';
import { handleGetSubscriptionPrice } from '../../ducks/stripe.duck';
import css from './PricingsPage.module.css';

// Features and tiers extracted from existing subscription components
const candidateTiers = ['Basic', 'Verified Access Member', 'Verified Plus Member'];
const candidatePricesLabel = ['Free', 'Pay-as-you-go', 'Plus'];

const candidateFeatures = [
  {
    name: 'Normal Price',
    description: 'Standard post-launch comparison price',
    values: ['—', '—', '$30 / yr (≈ $3 / mo)']
  },
  {
    name: 'Founding Member Launch Price',
    description: 'Early-adopter discounted launch pricing',
    values: ['Free ($0)', 'Pay-as-you-go', '$15 / yr (≈ $1.25 / mo)']
  },
  {
    name: 'Create & Publish Profile',
    description: 'Build and share your professional profile',
    values: ['✅ Basic public profile', '✅ Verified badge displayed', '✅ Verified badge displayed']
  },
  {
    name: 'Search Visibility',
    description: 'How your profile appears in searches',
    values: ['Listed in general results', 'Appears in "Verified Talent"', 'Appears in "Verified Talent"']
  },
  {
    name: 'Identity & Work Authorization Document Integrity Verification',
    description: 'Verify ID & the integrity of work authorization documents to appear in verified searches',
    values: ['—', 'Pay-per-verification ($10–15 each)', '✅ Discounted verifications ($8–12 each)']
  },
  {
    name: 'Certification Verification',
    description: 'Validate professional certifications and credentials',
    values: ['—', 'Pay-per-verification ($10–20 each)', '✅ Discounted verifications ($8–15 each)']
  },
  {
    name: 'Education / Experience Verification',
    description: 'Validate education and work experience',
    comingSoon: true,
    values: ['—', '—', '—']
  },
  {
    name: 'Reusable Verifiable Credentials / Wallet',
    description: 'Store and reuse verified credentials',
    values: ['—', 'Unlimited credentials (pay-per-issuance $5 each)', 'Unlimited credentials (discounted issuance $3 each)']
  },
  {
    name: 'Direct Messaging / Chat',
    description: 'Communicate directly with hiring managers or buyers',
    values: ['5 messages / month', 'Buy packs $10 = 25 msgs', 'Buy packs $5 = 25 msgs (50% off)']
  },
  {
    name: 'Apply to Job Posts',
    description: 'Submit applications for verified job opportunities',
    // values: ['Up to 3 active applications', 'Unlimited', 'Unlimited']
    values: ['Unlimited', 'Unlimited', 'Unlimited']
  },
  {
    name: 'External Profile & Credential Sharing',
    description: 'Share verified credentials or profile outside iParley (link / QR)',
    values: ['—', '—', '✅ Controlled external sharing enabled']
  },
  {
    name: 'Verification Reuse',
    description: 'Reuse credentials across employers',
    values: ['—', '✅ Stored in wallet', '✅ Reuse + share externally']
  }
];

const agencyTiers = ['Trust Access Vendor', 'Verified Delivery Vendor', 'Verified Plus Delivery Vendor'];

const agencyFeatures = [
  {
    name: 'Normal Price',
    description: 'Standard post-launch comparison point',
    values: ['—', '$150 / yr (≈ $15 / mo)', '$1,500 / yr (≈ $150 / mo)']
  },
  {
    name: 'Founding Partner Launch Price',
    description: 'Discounted launch pricing locked 2 yrs (limited slots)',
    values: ['Free ($0)', '$99 / yr or $10 / mo (20% monthly premium)', '$999 / yr or $100 / mo (20% monthly premium)']
  },
  {
    name: 'Founding Slot Availability',
    description: 'Creates exclusivity & urgency',
    values: ['Open to all', 'Limited to 50 Founding Vendors', 'Limited to 50 Founding Partners']
  },
  {
    name: 'Apply to Enterprise RFPs',
    description: 'Submit proposals for staffing or technology service RFPs posted by enterprises',
    values: ['❌ Not available', '$299 per RFP bid', '1 free bid / month (or 12 per year) + $149 per additional bid']
  },
  {
    name: 'Apply to Job Posts',
    description: 'Submit consultants for individual role openings',
    values: ['Up to 10 active job submissions', 'Unlimited', 'Unlimited']
  },
  {
    name: 'Business (KYB) Verification',
    description: 'Validate registration & EIN / tax ID',
    values: ['Pay-per-verification ($20–40)', '✅ Discounted ($15–30)', '✅ Discounted ($15–30)']
  },
  {
    name: 'Insurance Verification',
    description: 'Validate insurance coverage',
    comingSoon: true,
    values: ['—', '—', '—']
  },
  {
    name: 'Beneficial Owner Verification',
    description: 'Verify ownership / control for compliance',
    values: ['Pay-per-verification ($15–25)', '✅ Discounted ($10–20)', '✅ Discounted ($10–20)']
  },
  {
    name: 'Certifications / Diversity / Compliance Verification',
    description: 'Validate ISO, SOC 2, MWBE or industry certifications',
    values: ['Pay-per-verification ($20–50)', '✅ Discounted ($15–40)', '✅ Discounted ($15–40)']
  },
  {
    name: 'Past Project Verification',
    description: 'Verify completion of past projects or client work',
    comingSoon: true,
    values: ['—', '—', '—']
  },
  {
    name: 'External Credential & Profile Sharing',
    description: 'Share verified credentials / profiles externally (link / QR)',
    values: ['—', '—', '✅ Controlled external sharing enabled']
  },
  {
    name: 'Direct Messaging / Chat with Buyers',
    description: 'Communicate with enterprise hiring / procurement teams',
    values: ['Buy packs $10 = 25 msgs', 'Buy packs $5 = 25 msgs (50% off)', 'Unlimited chat or $5 = 25 msgs (50% off)']
  }
];

const customerTiers = ['Free / Pay-Per-Post', 'Verified Hiring', 'Trusted Sourcing'];

const customerFeatures = [
  {
    name: 'Normal Price',
    description: 'Standard post-launch price for comparison',
    values: ['—', '$3,000 / yr (≈ $250 / mo)', '$12,000 / yr (≈ $1,000 / mo)']
  },
  {
    name: 'Founding Member / Partner Price (Launch Offer)',
    description: 'Discounted launch pricing locked for 2 years (limited slots)',
    values: ['Free ($0)', '$2,400 / yr or $240 / mo (+20%)', '$9,000 / yr or $900 / mo (+20%)']
  },
  {
    name: 'Included Job & RFP Posts (Annual)',
    description: 'Bundled verified listings per subscription',
    values: ['—', '2 job slots or 24 jobs (30days) / year', '2 Job Slots + 1 RFP slot or 24 jobs+12 RFPs / year']
  },
  {
    name: 'Flat Job Trust Fee (Additional) for 30 days',
    description: 'Trust layer fee for extra jobs',
    values: ['$200 / job slot', '$120 / jobslot /30days', '$120 / job slot /30days']
  },
  {
    name: 'Flat RFP Trust Fee (Additional) for 30 days',
    description: 'Covers supplier verification + bid integrity',
    values: ['$1,000 / RFP slot', '—', '$600 / RFP Slot/30days']
  },
  {
    name: 'Integrity Check Credits',
    description: '10 credits per job slot included; use to run integrity checks on candidates. Add-ons available for all tiers.',
    values: [
      '10 credits / job slot purchased • Add-ons: $5 = 1 credit',
      '20 included credits (2 job slots × 10) • Add-ons: $5 = 1 credit',
      '20 included credits (2 job slots × 10) • Add-ons: $5 = 1 credit'
    ]
  },
  {
    name: 'SkillFit Check Credits',
    description: '10 SkillFit credits per job slot included; use to run SkillFit skills assessments on candidates. Add-ons available for all tiers. A credit is charged when the interview starts, not when the invitation is sent.',
    values: [
      '10 SkillFit credits / job slot purchased • Add-ons: $5 = 1 credit',
      '20 included SkillFit credits (2 job slots × 10) • Add-ons: $5 = 1 credit',
      '20 included SkillFit credits (2 job slots × 10) • Add-ons: $5 = 1 credit'
    ]
  },
  {
    name: 'Direct Messaging / Chat Packages',
    description: 'Communicate securely with candidates & suppliers',
    values: ['25 msgs / mo free • Extra: $5 = 25 msgs', '100 msgs / mo free • Extra: $5 = 100 msgs (75% off)', '500 msgs/month or $5 = 100 msgs (75% off)']
  }
];

const formatPrice = (price) => {
  if (!price || !price.unit_amount) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price?.currency?.toUpperCase(),
  }).format(price?.unit_amount / 100);
};

export const PricingsPageComponent = props => {
  const { subscriptionPrices, getSubscriptionPrices, currentUser } = props;
  const intl = useIntl();
  const [selectedUserType, setSelectedUserType] = useState('candidate');

  useEffect(() => {
    if (typeof getSubscriptionPrices === 'function') {
      getSubscriptionPrices();
    }
  }, [getSubscriptionPrices]);

  // Helper to find price by product name (uses naming from StripePayoutPage)
  const findPrice = (name) => subscriptionPrices?.prices?.find(p => p?.product?.name === name) || null;

  // Candidate price lookups
  const candidateMonthly = findPrice('iParley Plus monthly plan for candidate');
  const candidateYearly = findPrice('iParley Plus yearly plan for candidate');

  // Agency price lookups
  const agencyMonthly = findPrice('Verified delivery vendor monthly plan');
  const agencyYearly = findPrice('Verified delivery vendor yearly plan');
  const agencyPlusMonthly = findPrice('Verified plus delivery vendor monthly plan');
  const agencyPlusYearly = findPrice('Verified plus delivery vendor yearly plan');

  // Customer price lookups
  const customerHiringMonthly = findPrice('Verified hiring (Customer plan per month)');
  const customerHiringYearly = findPrice('Verified hiring (Customer plan per year)');
  const customerSourcingMonthly = findPrice('Trusted sourcing (Customer plan per month)');
  const customerSourcingYearly = findPrice('Trusted sourcing (Customer plan per year)');

  // Get current usertype data based on selection
  const getCurrentData = () => {
    switch (selectedUserType) {
      case 'candidate':
        return { tiers: candidateTiers, features: candidateFeatures, pricesLabel: candidatePricesLabel, title: 'Candidate Pricing' };
      case 'agency':
        return { tiers: agencyTiers, features: agencyFeatures, pricesLabel: null, title: 'Supplier Pricing' };
      case 'customer':
        return { tiers: customerTiers, features: customerFeatures, pricesLabel: null, title: 'Customer Pricing' };
      default:
        return { tiers: candidateTiers, features: candidateFeatures, pricesLabel: candidatePricesLabel, title: 'Candidate Pricing' };
    }
  };

  const currentData = getCurrentData();

  return (
    <Page title={intl.formatMessage({ id: 'PricingsPage.title', defaultMessage: 'Pricings' })}>
      <LayoutSingleColumn
        topbar={
          <TopbarContainer mobileRootClassName={css.mobileTopbar} desktopClassName={css.desktopTopbar} />
        }
        footer={<FooterContainer />}
      >
        <div className={css.pricingsPage}>
          <div className={css.header}>
            <h1 className={css.mainTitle}>Choose Your Plan</h1>
            <p className={css.subtitle}>Select your user type to view pricing options tailored for you</p>
          </div>

          {/* Usertype Tabs */}
          <div className={css.tabContainer}>
            <button
              className={`${css.tab} ${selectedUserType === 'candidate' ? css.tabActive : ''}`}
              onClick={() => setSelectedUserType('candidate')}
            >
              <div className={css.tabIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className={css.tabContent}>
                <div className={css.tabTitle}>Candidate</div>
                <div className={css.tabDescription}>For job seekers</div>
              </div>
            </button>

            <button
              className={`${css.tab} ${selectedUserType === 'agency' ? css.tabActive : ''}`}
              onClick={() => setSelectedUserType('agency')}
            >
              <div className={css.tabIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 9h18M9 21V9" />
                </svg>
              </div>
              <div className={css.tabContent}>
                <div className={css.tabTitle}>Supplier</div>
                <div className={css.tabDescription}>For staffing suppliers</div>
              </div>
            </button>

            <button
              className={`${css.tab} ${selectedUserType === 'customer' ? css.tabActive : ''}`}
              onClick={() => setSelectedUserType('customer')}
            >
              <div className={css.tabIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </div>
              <div className={css.tabContent}>
                <div className={css.tabTitle}>Customer</div>
                <div className={css.tabDescription}>For enterprises</div>
              </div>
            </button>
          </div>

          {/* Pricing Table */}
          <div className={css.section}>
            <h2 className={css.sectionTitle}>{currentData.title}</h2>
            <div className={css.tableWrapper}>
              <table className={css.pricingTable}>
                <thead>
                  <tr>
                    <th className={css.featureName}>Features</th>
                    {currentData.tiers.map((t, i) => (
                      <th key={t} className={css.priceCell}>
                        <div className={css.tierName}>{t}</div>
                        {currentData.pricesLabel && (
                          <div className={css.tierPrice}>{currentData.pricesLabel[i]}</div>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {currentData.features.map((feature, featureIdx) => (
                    <tr key={feature.name} className={featureIdx % 2 === 0 ? css.evenRow : css.oddRow}>
                      <td className={css.featureCell}>
                        <div className={css.featureNameText}>
                          {feature.name}
                          {feature.comingSoon && <span className={css.comingSoon}>Coming soon</span>}
                        </div>
                        <div className={css.featureDescription}>{feature.description}</div>
                      </td>
                      {feature.values.map((value, idx) => (
                        <td key={idx} className={`${css.valueCell} ${value === '—' ? css.empty : css.featureValue}`}>
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {currentUser && (
            <div className={css.actionSection}>
              <Button type="primary" size="large" href="/account/subscriptions" className={css.manageButton}>
                Manage Subscriptions
              </Button>
            </div>
          )}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  subscriptionPrices: state.stripe.prices,
  currentUser: state.user.currentUser,
});

const mapDispatchToProps = dispatch => ({
  getSubscriptionPrices: () => dispatch(handleGetSubscriptionPrice()),
});

const PricingsPage = compose(connect(mapStateToProps, mapDispatchToProps))(PricingsPageComponent);

export default PricingsPage;
